import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Web as WebIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Timeline as TimelineIcon,
  Dns as WorkersIcon,
  ArrowBack as ArrowBackIcon,
  ViewInAr as ContainersIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";
import appLogo from "../../assets/logo.png";
import { SPACING, BORDER_RADIUS, ICON_SIZES } from "../../theme/constants";
import { flexCenter } from "../../theme/responsive";

const adminMenuItems = [
  { text: "Overview", icon: <DashboardIcon />, path: "/admin" },
  { text: "Users", icon: <PeopleIcon />, path: "/admin/users" },
  { text: "WP Sites", icon: <WebIcon />, path: "/admin/sites" },
  { text: "Applications", icon: <AppsIcon />, path: "/admin/applications" },
  { text: "Containers", icon: <ContainersIcon />, path: "/admin/containers" },
  { text: "Workers", icon: <WorkersIcon />, path: "/admin/workers" },
  { text: "Plans", icon: <PaymentIcon />, path: "/admin/plans" },
  { text: "Backups", icon: <StorageIcon />, path: "/admin/backups" },
  { text: "Activities", icon: <TimelineIcon />, path: "/admin/activities" },
  { text: "Settings", icon: <SettingsIcon />, path: "/admin/settings" },
];

export default function AdminSidebar({ user, isMobile, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onNavigate) {
      onNavigate();
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: SPACING.md,
          py: SPACING.sm,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: { xs: 48, sm: 48 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: SPACING.sm }}>
          <Box
            sx={{
              width: ICON_SIZES.xxl,
              height: ICON_SIZES.xxl,
              borderRadius: BORDER_RADIUS.sm,
              ...flexCenter,
              flexShrink: 0,
            }}
          >
            <img
              src={appLogo}
              alt="WP Spark Logo"
              style={{ width: ICON_SIZES.xxl, height: ICON_SIZES.xxl }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
          >
            WpSpark
          </Typography>
        </Box>
        <Chip
          label="Admin"
          size="small"
          color="error"
          sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
        />
      </Box>

      <Divider />

      {/* Back to Dashboard */}
      <List sx={{ px: SPACING.sm, pt: SPACING.xs, pb: 0 }}>
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            onClick={() => handleNavigation("/dashboard")}
            sx={{
              borderRadius: 1,
              py: 0.75,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "text.secondary",
                minWidth: 32,
                "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
              }}
            >
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText
              primary="Back to Dashboard"
              primaryTypographyProps={{
                fontSize: "0.8125rem",
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />

      {/* Admin Navigation Menu */}
      <List sx={{ flex: 1, px: SPACING.sm, py: SPACING.xs }}>
        {adminMenuItems.map((item) => {
          const isActive =
            item.path === "/admin"
              ? location.pathname === "/admin"
              : location.pathname.startsWith(item.path);

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 1,
                  py: 0.75,
                  backgroundColor: isActive ? "error.main" : "transparent",
                  color: isActive ? "white" : "text.primary",
                  "&:hover": {
                    backgroundColor: isActive ? "error.dark" : "action.hover",
                  },
                  transition: "all 0.15s",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "white" : "text.secondary",
                    minWidth: 32,
                    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.8125rem",
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* User Info Section */}
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "grey.800" : "grey.50",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "error.main",
              fontSize: "0.8125rem",
            }}
          >
            {user?.firstName?.[0] || "A"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "0.8125rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.6875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              Administrator
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
